import { Injectable } from '@angular/core';

import {
  MapCluster,
  MapProperty,
} from 'src/app/_shared/interface/property.interface';

import { NumberFormatterPipe } from 'src/app/_shared/pipe/number.pipe';

import { Marker } from '../class/marker.class';
import { MapState } from '../state/map.state';
import { MarkerState } from '../state/marker.state';
import { PropertyState } from '../state/property.state';
import { MapService } from './map.service';

@Injectable({
  providedIn: 'root',
})
export class MarkerService {
  constructor(
    private mapService: MapService,
    private mapState: MapState,
    private markerState: MarkerState,
    private numberFormatter: NumberFormatterPipe,
    private propertyState: PropertyState
  ) {
    this.initMarkerGeneration();
  }

  initMarkerGeneration(): void {
    this.propertyState.mapClusterSet$.subscribe(
      (clusterSet: MapCluster) => {
        if (!this.markerState.renderMarkerValue) {
          return;
        }

        void this.generateClusterMarkers(clusterSet);
      }
    );

    this.propertyState.mapPropertySet$.subscribe(
      (propertySet: MapProperty) => {
        if (!this.markerState.renderMarkerValue) {
          return;
        }

        void this.generatePropertyMarkers(propertySet);
      }
    );
  }

  private async generateClusterMarkers(
    clusterSet: MapCluster
  ): Promise<void> {
    if (!clusterSet || !Object.keys(clusterSet).length) {
      this.markerState.mapMarkerListValue = null;
      return;
    }

    const map = this.mapState.workspaceMap;

    if (!map) {
      console.warn(
        'Workspace map is unavailable. Cluster markers cannot be created.'
      );

      return;
    }

    try {
      const markerSet: Record<string, any> = {};
      const keys = Object.keys(clusterSet);

      await Promise.all(
        keys.map(async (key: string) => {
          const cluster = clusterSet[key];

          const marker = await Marker.create(
            cluster.Latitude,
            cluster.Longitude,
            map,
            'cluster',
            'filtered',
            `${cluster.PropertyCount || 1}`,
            cluster
          );

          marker.addListener('click', () => {
            map.setCenter(marker.getPosition());

            const currentZoom = map.getZoom() || 0;
            map.setZoom(currentZoom + 2);
          });

          markerSet[key] = marker;
        })
      );

      this.markerState.mapMarkerListValue = markerSet;
    } catch (error) {
      console.error('Failed to generate cluster markers:', error);
      this.markerState.mapMarkerListValue = null;
    }
  }

  private async generatePropertyMarkers(
    propertySet: MapProperty
  ): Promise<void> {
    if (!propertySet || !Object.keys(propertySet).length) {
      this.markerState.mapMarkerListValue = null;
      return;
    }

    const map = this.mapState.workspaceMap;

    if (!map) {
      console.warn(
        'Workspace map is unavailable. Property markers cannot be created.'
      );

      return;
    }

    try {
      const markerSet: Record<string, any> = {};
      const keys = Object.keys(propertySet);

      await Promise.all(
        keys.map(async (key: string) => {
          const property = propertySet[key];

          const markerValue =
            property.propertyCount === 1
              ? `${this.numberFormatter.transform(
                property.propertyPrice
              ) || ''
              }`
              : `${property.propertyCount} No.`;

          const marker = await Marker.create(
            property.latitude,
            property.longitude,
            map,
            'property',
            property.filterStyleClass,
            markerValue,
            property
          );

          marker.addListener('click', () => {
            this.mapService.openInfoWindow(marker);
          });

          markerSet[key] = marker;
        })
      );

      this.markerState.mapMarkerListValue = markerSet;
    } catch (error) {
      console.error('Failed to generate property markers:', error);
      this.markerState.mapMarkerListValue = null;
    }
  }

  updateMarkersInDrawing(
    drawingOverlay: any,
    drawingType: string
  ): void {
    if (
      !this.markerState.googleMarkerListValue.length ||
      !drawingOverlay ||
      !drawingType
    ) {
      return;
    }

    const selectedMarkers: any[] = [];

    this.markerState.googleMarkerListValue.forEach(
      (marker: any) => {
        if (drawingType === 'circle') {
          const distanceFromCenter =
            google.maps.geometry.spherical.computeDistanceBetween(
              marker.getPosition(),
              drawingOverlay.getCenter()
            );

          if (distanceFromCenter > drawingOverlay.getRadius()) {
            marker.setVisible(false);
          } else {
            marker.setVisible(true);
            selectedMarkers.push(marker.id);
          }
        } else if (drawingType === 'polygon') {
          const isInPolygon =
            google.maps.geometry.poly.containsLocation(
              marker.getPosition(),
              drawingOverlay
            );

          if (!isInPolygon) {
            marker.setVisible(false);
          } else {
            marker.setVisible(true);
            selectedMarkers.push(marker.id);
          }
        }
      }
    );
  }

  isEmptyObject(obj: any): boolean {
    return !!obj && Object.keys(obj).length === 0;
  }

  formatMarkerLabel(value: number): string {
    return this.numberFormatter.transform(value);
  }
}